/**
 * The one course frame (#1393 R6, `docs/delivery/opus/DESK-FIRST.md` contract 4).
 *
 * Every course view, Desk included, draws inside the same shell: one wrapper,
 * one Home link, one band treatment and one arrival-focus rule. Leave
 * coordination is the `AssignmentDraftGuard` around the whole workspace, so a
 * draft's report survives moving between Desk and a view. Each view still
 * reads its own projection: Course Home never gates Desk, and the Desk read
 * never gates a view.
 */
import type { ReactNode, RefObject } from "react";
import { Link } from "react-router";
import { EditionBand } from "@/components/workspace/EditionBand";

/** One page: the frame, then boxes on the bento gap, in the portal's own bounds. */
export const PAGE = "mx-auto flex w-full max-w-[90rem] flex-col gap-[var(--layout-bento-gap)] px-4 pt-4 pb-16 sm:px-7 sm:pt-6";

/**
 * The frame's wrapper and Home link around whatever the view draws. Every
 * view renders this same element, so a move between views that share it (an
 * exact session to its record's sheet) keeps the node the record owner
 * returns focus into. The exact session draws its own Return in place of the
 * Home link (`home={false}`).
 */
export function CourseFrameShell({ perspective, workspaceHref, hidden, notice, home = true, children }: {
  perspective: string;
  workspaceHref: string;
  hidden?: boolean;
  notice?: ReactNode;
  home?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0" hidden={hidden} data-course-workspace-frame={perspective}>
      {notice}
      {home && <Link to={workspaceHref} className="sr-only focus:not-sr-only focus:inline-flex focus:min-h-11 focus:items-center">
        Home
      </Link>}
      {children}
    </div>
  );
}

/** The edition band as the frame draws it: its `h1` takes the frame's focus ring. */
export function CourseBand({ bandRef, code, eyebrow, title, meta, action }: {
  bandRef: RefObject<HTMLDivElement | null>;
  code: string;
  eyebrow?: ReactNode;
  title: string;
  meta?: string;
  action?: ReactNode;
}) {
  return (
    <div ref={bandRef} className="contents [&_h1]:outline-none [&_h1:focus-visible]:rounded-xs [&_h1:focus-visible]:ring-2 [&_h1:focus-visible]:ring-ring">
      <EditionBand code={code} eyebrow={eyebrow} title={title} meta={meta} action={action} />
    </div>
  );
}
